import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  buildConfusionMatrix,
  buildDisciplineBreakdown,
  buildFleetSummary,
  buildTrendByMonth,
} from '../lib/aggregates.ts';
import { listCorpusJobsWithPages } from '../lib/db.ts';
import type { CorpusJobWithPages } from '../lib/db.ts';
import { gradeJob } from '../lib/grading.ts';
import type { JobGrade } from '../lib/grading.ts';
import { ConfusionMatrix } from '../components/training/ConfusionMatrix.tsx';
import { CorpusJobList } from '../components/training/CorpusJobList.tsx';
import type { CorpusJobRow } from '../components/training/CorpusJobList.tsx';
import { DisciplineBreakdown } from '../components/training/DisciplineBreakdown.tsx';
import { FleetSummary } from '../components/training/FleetSummary.tsx';
import { TrendByMonth } from '../components/training/TrendByMonth.tsx';

type LoadState =
  | { kind: 'loading' }
  | { kind: 'ready'; jobs: readonly CorpusJobWithPages[] }
  | { kind: 'error'; message: string };

export function Training() {
  const [state, setState] = useState<LoadState>({ kind: 'loading' });

  useEffect(() => {
    let active = true;
    async function load(): Promise<void> {
      try {
        const jobs = await listCorpusJobsWithPages();
        if (active) setState({ kind: 'ready', jobs });
      } catch (err: unknown) {
        const message = err instanceof Error ? err.message : 'Unknown error';
        if (active) setState({ kind: 'error', message });
      }
    }
    void load();
    return () => {
      active = false;
    };
  }, []);

  const computed = useMemo(() => {
    if (state.kind !== 'ready') return null;
    const submissions = state.jobs.map((j) => j.submission);
    const grades: JobGrade[] = [];
    const rows: CorpusJobRow[] = [];
    for (const job of state.jobs) {
      const grade = gradeJob(job.submission, job.pages);
      if (!grade) continue;
      grades.push(grade);
      rows.push({ submission: job.submission, grade });
    }
    return {
      summary: buildFleetSummary(grades, submissions),
      breakdown: buildDisciplineBreakdown(grades),
      matrix: buildConfusionMatrix(grades),
      trend: buildTrendByMonth(grades, submissions),
      rows,
    };
  }, [state]);

  return (
    <div style={{ maxWidth: 1100 }}>
      <div style={headerStyle}>
        <h1 style={{ fontFamily: "'Syne', sans-serif", fontSize: 28, fontWeight: 800, margin: 0 }}>
          Training
        </h1>
        <div style={{ color: '#525870', fontSize: 12, fontFamily: "'JetBrains Mono', monospace" }}>
          Hyperbolic Chamber · grading dashboard
        </div>
      </div>

      {state.kind === 'loading' && (
        <div style={{ color: '#525870', fontSize: 13 }}>Loading…</div>
      )}

      {state.kind === 'error' && (
        <div style={errorStyle}>{state.message}</div>
      )}

      {state.kind === 'ready' && computed !== null && computed.rows.length === 0 && (
        <EmptyState />
      )}

      {state.kind === 'ready' && computed !== null && computed.rows.length > 0 && (
        <div style={gridStyle}>
          <div style={{ gridColumn: '1 / -1' }}>
            <FleetSummary summary={computed.summary} />
          </div>
          <div style={{ gridColumn: '1 / -1' }}>
            <TrendByMonth buckets={computed.trend} />
          </div>
          <div style={{ gridColumn: '1 / -1' }}>
            <ConfusionMatrix matrix={computed.matrix} />
          </div>
          <div>
            <DisciplineBreakdown rows={computed.breakdown} />
          </div>
          <div>
            <CorpusJobList rows={computed.rows} />
          </div>
        </div>
      )}
    </div>
  );
}

function EmptyState() {
  return (
    <div style={emptyStyle}>
      <div style={{ fontFamily: "'Syne', sans-serif", fontSize: 18, fontWeight: 700, color: '#F0F2FF', marginBottom: 8 }}>
        No corpus jobs yet
      </div>
      <div style={{ fontSize: 13, lineHeight: 1.6, color: '#B8BDD6' }}>
        Promote a reviewed job to start grading.{' '}
        <Link to="/app/jobs" style={{ color: '#00E87A' }}>
          Open jobs →
        </Link>
      </div>
    </div>
  );
}

const headerStyle: React.CSSProperties = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'baseline',
  marginBottom: 22,
};

const gridStyle: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(360px, 1fr))',
  gap: 18,
};

const emptyStyle: React.CSSProperties = {
  border: '1px dashed rgba(255,255,255,0.12)',
  borderRadius: 10,
  padding: '36px 28px',
  textAlign: 'center',
  background: '#0D0F1C',
};

const errorStyle: React.CSSProperties = {
  color: '#FF6B7A',
  fontSize: 13,
  padding: 16,
  border: '1px solid rgba(255,107,122,0.3)',
  borderRadius: 8,
  background: '#0D0F1C',
};
